import { Services, getServicePathToAPI, joinURLPath } from '@sigma-frontend-libs/commons'
import { OrderDirections } from '@sigmasms/shared-types'

import { globalErrorHandler, labelModes, permissionPresets, proxy, request } from '@/utils'

import render from './render'

const makeSettingPath = service => {
  return `${Services.sendings}.${service}Check`
}
const pathToAPI = getServicePathToAPI(Services.settingsUser)

export default {
  name: 'UsersAssociationsByType',

  mixins: [ proxy({ type: 'object' }) ],

  data() {
    return {
      type: undefined,

      services: [ Services.sendernames, Services.patterns ],

      checks: {
        [Services.sendernames]: {},
        [Services.patterns]: {}
      },
      initChecks: {
        [Services.sendernames]: {},
        [Services.patterns]: {}
      },

      loading: {
        get: false,
        set: {
          [Services.sendernames]: false,
          [Services.patterns]: false
        }
      }
    }
  },

  computed: {
    canUpdateUsers() {
      return this.checkPermissions(`advanced.${Services.users}.update`, permissionPresets.meUp)
    },
    canGetSettingsUser() {
      return this.checkPermissions(`advanced.${Services.settingsUser}.get`, permissionPresets.meUp)
    },
    canUpdateSettingsUser() {
      return this.checkPermissions(`advanced.${Services.settingsUser}.set`, permissionPresets.meUp)
    },
    canReadWriteSettingsUser() {
      return this.services.every(service => {
        return this.getGranularPermission(makeSettingPath(service), [ 'default', 'read', 'write' ])
      })
    },

    types() {
      return this.getSendingTypesByPermission(`advanced.${Services.users}.update`) || []
    },

    hasTypes() {
      return Array.isArray(this.types) && this.types.length > 0
    },

    selectPropsByService() {
      const result = {}

      const commonProps = {
        picks: [ 'moderation', 'type', 'isActive', 'Owner' ],
        disabled: !this.canUpdateUsers,
        labelMode: labelModes.hidden,
        dense: true,
        multiple: true,
        clearable: true,
        autocomplete: true,
        details: false
      }

      for (const service of this.services) {
        result[service] = {
          ...commonProps,
          label: this.getTranslate(`${Services.users}.labels.${service}`),
          service,
          itemTitle: 'title',
          query: {
            $scope: [ 'Owner' ],
            type: this.type
          }
        }

        switch (service) {
          case Services.sendernames: {
            result[service].itemTitle = 'name'
            break
          }

          case Services.patterns: {
            result[service].order = OrderDirections.DESC
            result[service].query.isActive = true
            break
          }
        }
      }

      return result
    }
  },

  async mounted() {
    if (this.hasTypes) {
      await Promise.all(this.services.map(async service => {
        for (const type of this.types) {
          this.$set(this.checks[service], type, false)
          this.$set(this.initChecks[service], type, false)
        }

        await this.getSetting(service)
      }))

      this.type = this.types[0]
    }
  },

  methods: {
    makeSettingPath,

    checkGranularPermissions(settingPath, presets) {
      return this.checkPermissions(`granular.me.${Services.settingsUser}:${settingPath}`, presets)
    },
    getGranularWritePermission(settingPath) {
      return this.checkGranularPermissions(settingPath, [ 'default', 'write' ])
    },

    hasCheckChangesByService(service) {
      if (!this.type) {
        return false
      }

      return this.checks[service][this.type] !== this.initChecks[service][this.type]
    },

    isCheckButtonDisabled(service) {
      const permission = !this.getGranularWritePermission(makeSettingPath(service))
      const noChanges = !this.hasCheckChangesByService(service)
      const loading = this.loading.set[service]

      return permission || noChanges || loading
    },

    async getSetting(service) {
      const settingPath = makeSettingPath(service)
      const canGetSpecificSetting = this.getGranularWritePermission(settingPath)
      if (this.canGetSettingsUser && canGetSpecificSetting) {
        try {
          this.loading.get = true
          for (const type of this.types) {
            this.$set(this.checks[service], type, false)
            this.$set(this.initChecks[service], type, false)
          }

          const data = await request.get(joinURLPath(pathToAPI, this.proxy.id, settingPath))
          if (data) {
            for (const type of data) {
              this.$set(this.checks[service], type, true)
              this.$set(this.initChecks[service], type, true)
            }
          }
        } catch (error) {
          globalErrorHandler(error)
        } finally {
          this.loading.get = false
        }
      }
    },

    async updateSetting(service) {
      const settingPath = makeSettingPath(service)
      const canUpdateSpecificSetting = this.getGranularWritePermission(settingPath)
      if (this.canUpdateSettingsUser && canUpdateSpecificSetting) {
        try {
          this.loading.set[service] = true
          const value = []
          for (const type in this.checks[service]) {
            if (this.checks[service][type] === true && !~value.indexOf(type)) {
              value.push(type)
            }
          }

          const response = await request.update(joinURLPath(pathToAPI, this.proxy.id, settingPath), { body: { value } })
          if (response) {
            const types = response.value || []
            const typesSet = new Set(types)

            for (const type of this.types) {
              const hasType = typesSet.has(type)
              this.$set(this.checks[service], type, hasType)
              this.$set(this.initChecks[service], type, hasType)
            }
          }
        } catch (error) {
          globalErrorHandler(error)
        } finally {
          this.loading.set[service] = false
        }
      }
    }
  },

  render
}
